import { memo, useCallback } from 'react';

import { Switch } from '@/components/ui/switch';

import { pricingPlans } from '@/features/pricing/constants/plans';
import { PricingPlan } from '@/features/pricing/types';

import { COPY } from '@/shared/constants/copies';

import { cn } from '@/lib/utils';

export const BillingCycleToggle = memo(function BillingCycleToggle({
	plan,
	onPlanChange,
	className,
	compact = false,
}: {
	plan: PricingPlan;
	onPlanChange: (plan: PricingPlan) => void;
	className?: string;
	compact?: boolean;
}) {
	const isYearly = plan.id.endsWith('_YEARLY');

	const handleCheckedChange = useCallback(
		(checked: boolean) => {
			onPlanChange(checked ? pricingPlans.pro_yearly : pricingPlans.pro_monthly);
		},
		[onPlanChange]
	);

	const selectMonthly = useCallback(() => {
		onPlanChange(pricingPlans.pro_monthly);
	}, [onPlanChange]);

	const selectYearly = useCallback(() => {
		onPlanChange(pricingPlans.pro_yearly);
	}, [onPlanChange]);

	return (
		<div className={cn('mb-12 flex justify-center', className)}>
			<div className="flex items-center gap-3">
				<button
					type="button"
					onClick={selectMonthly}
					className={cn(
						'text-sm font-medium',
						isYearly ? 'text-muted-foreground' : 'text-primary'
					)}
				>
					{COPY.pricing.billingMonthly}
				</button>
				<Switch
					checked={isYearly}
					onCheckedChange={handleCheckedChange}
					size={compact ? 'sm' : 'default'}
					aria-label={COPY.pricing.billingYearly}
					className="shrink-0"
				/>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={selectYearly}
						className={cn(
							'text-sm font-medium',
							isYearly ? 'text-primary' : 'text-muted-foreground'
						)}
					>
						{COPY.pricing.billingYearly}
					</button>
					<p className="rounded-md bg-emerald-50 px-2 py-0.5 text-sm font-medium text-emerald-700">
						{COPY.pricing.billingYearlySave}
					</p>
				</div>
			</div>
		</div>
	);
});
